/**
 * Notification Redux Thunks
 * 
 * Async actions for notification operations using Redux Toolkit's createAsyncThunk.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { notificationService } from '@/app/features/notifications/services/notificationService';
import { NotificationService } from '@/app/features/notifications/services/notificationService';
import {
  GetUserNotificationsRequest,
  GetUserNotificationsResponse,
} from '@/app/features/notifications/types/notification.types';

/**
 * Fetch user notifications with pagination and filtering
 */
export const fetchNotifications = createAsyncThunk<
  GetUserNotificationsResponse,
  GetUserNotificationsRequest | undefined,
  { rejectValue: string }
>(
  'notification/fetchNotifications',
  async (request = {}, { rejectWithValue }) => {
    try {
      const response = await notificationService.getUserNotifications(request);
      return response;
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch unread notification count
 */
export const fetchUnreadCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>(
  'notification/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const count = await notificationService.getUnreadCount();
      return count;
    } catch (error) {
      // Check if it's a 401 error (authentication issue during account switch)
      if (NotificationService.isAuthError(error)) {
        console.log('[fetchUnreadCount] Authentication error (likely during account switch), will retry later');
        // Return 0 instead of rejecting to avoid error toast during account switch
        return 0;
      }
      
      const message = NotificationService.getErrorMessage(error);
      console.error('[fetchUnreadCount] Failed to fetch unread count:', message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Mark notification as read
 */
export const markAsRead = createAsyncThunk<
  number, // Returns notification ID
  number, // Notification ID parameter
  { rejectValue: string }
>(
  'notification/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      await notificationService.markAsRead(notificationId);
      return notificationId;
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Dismiss notification
 */
export const dismissNotification = createAsyncThunk<
  number, // Returns notification ID
  number, // Notification ID parameter
  { rejectValue: string }
>(
  'notification/dismissNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      await notificationService.dismissNotification(notificationId);
      return notificationId;
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Mark all notifications as read
 */
export const markAllAsRead = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'notification/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Clear all read notifications
 */
export const clearAllRead = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'notification/clearAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await notificationService.clearAllRead();
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch user notifications (alias for compatibility)
 */
export const fetchUserNotifications = fetchNotifications;

/**
 * Mark notification as read (alias for compatibility)
 */
export const markNotificationAsRead = markAsRead;

/**
 * Mark multiple notifications as read
 */
export const markNotificationsAsRead = createAsyncThunk<
  number[], // Returns array of notification IDs
  number[], // Array of notification IDs parameter
  { rejectValue: string }
>(
  'notification/markNotificationsAsRead',
  async (notificationIds, { rejectWithValue }) => {
    try {
      // Mark each notification as read
      await Promise.all(
        notificationIds.map(id => notificationService.markAsRead(id))
      );
      return notificationIds;
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Delete a single notification
 */
export const deleteNotification = createAsyncThunk<
  number, // Returns notification ID
  number, // Notification ID parameter
  { rejectValue: string }
>(
  'notification/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      await notificationService.dismissNotification(notificationId);
      return notificationId;
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Delete multiple notifications
 */
export const deleteNotifications = createAsyncThunk<
  number[], // Returns array of notification IDs
  number[], // Array of notification IDs parameter
  { rejectValue: string }
>(
  'notification/deleteNotifications',
  async (notificationIds, { rejectWithValue }) => {
    try {
      // Delete each notification
      await Promise.all(
        notificationIds.map(id => notificationService.dismissNotification(id))
      );
      return notificationIds;
    } catch (error) {
      const message = NotificationService.getErrorMessage(error);
      return rejectWithValue(message);
    }
  }
);
